import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import {
  get,
  isArray,
  isEmpty,
  isNumber,
  isObject,
  isUndefined,
  pickBy,
  keys,
  compact,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro,
  GridCellEditStopParams,
  GridCellEditStopReasons,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  MuiEvent,
} from '@mui/x-data-grid-pro';
import { Box, Theme } from '@mui/material';
import { ChevronRight, ExpandMore, DeleteForever as RestoreIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { IMuiGrid } from './_types';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { Papeer } from 'components/Papeer/Papeer';
import {
  DATA_GRID_DEFAULT_SORTING,
  DATA_GRID_ROW_HEIGHT,
  NOTIFICATIONS_STATE_NEW,
} from 'constants/constants';
import { useFilterableColumns } from 'utils/hooks/useFilterableColumns';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import useGridLocalization from 'components/SearchResultsMUI/useGridLocalization';
import Button from 'components/Buttons/Button';

const dangerBackground = '#efc1c1';
const dangerBackgroundHover = '#d7adad';
const dangerBackgroundSelected = '#bf9a9a';
const dangerBackgroundSelectedHover = '#ab8a8a';
const darkDangerBackground = '#873336';
const darkDangerBackgroundHover = '#792d30';
const darkDangerBackgroundSelected = '#6c282b';
const darkDangerBackgroundSelectedHover = '#5e2325';
const greenRow = '#5FB3D3';

export const MuiGrid: React.FC<IMuiGrid> = ({
  gridKey,
  rows,
  columns,
  getRowId,
  rowDetail,
  autoHeight,
  rowHeight, // for multiple columns value
  defaultHiddenColumnNames,
  toolbar, // own toolbar
  rowSelecting,
  dataTour,
  disableColumnMenu,
  hideGridToolbarColumnsButton,
  hideGridToolbarColumnsFilters,
  withoutMaxHight,
  hideFooter,
  flex,
  hideHeaderFilters,
  sxPapeer,
  initialSortMode,
  onRowClick,
}) => {
  const { t } = useTranslation('Grid');

  const { compactMode } = useAppInfo();
  const theme = useTheme() as Theme;
  const isDarkMode = theme.palette.mode === 'dark';

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    getTogglableColumns,
    gridSortModel,
    changeWidthColumn,
    sortByColumn,
    updateColumnOrder,
    setAndStore,
  } = useMuiGrid(gridKey);

  const gridLocalization = useGridLocalization();

  const columnVisibilityModelWithDefaultHidden = {
    ...(isUndefined(columnVisibilityModel) ? defaultHiddenColumnNames : columnVisibilityModel),
  };

  const [filterModel, setFilterModel] = useState<any>(undefined);
  const handleFilterModelChange = useCallback((newModel: any) => {
    setFilterModel(newModel);
  }, []);

  const indexShiftForReordering =
    (rowDetail?.showRowDetail ? 1 : 0) + (rowSelecting?.selecting ? 1 : 0);

  const onRestoreDefaultConfiguration = useCallback(async () => {
    setAndStore({});
  }, [setAndStore]);

  const QuickSearchToolbar = (props: any) => {
    return (
      <Box sx={{ p: 0.5 }}>
        {!props.hideGridToolbarColumnsButton && (
          <span data-tour="common-gridColumns">
            <GridToolbarColumnsButton />
          </span>
        )}
        {!props.hideGridToolbarColumnsFilters && (
          <span data-tour="common-gridFilters">
            <GridToolbarFilterButton />
          </span>
        )}
        <span data-tour="common-gridRestoreDefaultConfiguration">
          <Button
            size="small"
            color="primary"
            onClick={onRestoreDefaultConfiguration}
            data-pw="restore-default-configuration-button"
          >
            <RestoreIcon />
            {t('restoreDefaultConfiguration')}
          </Button>
        </span>
        {/* <GridToolbarDensitySelector /> */}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: isNumber(flex) ? flex : 1,
        display: 'flex',
        ...(!withoutMaxHight ? { maxHeight: '100%', overflow: 'hidden' } : {}),
      }}
    >
      <Papeer
        id={gridKey}
        sx={{
          flex: 1,
          ...(!withoutMaxHight ? { maxHeight: '100%' } : {}),
          maxWidth: '100%',
          ...(compactMode ? { p: 0 } : {}),
          ...sxPapeer,
        }}
      >
        <Box
          data-tour={dataTour || gridKey}
          sx={{
            height: '100% !important',
            width: '100% !important',
            '& .css.boldRow': { fontWeight: 'bold' },
            '& .css.greenRow': {
              backgroundColor: greenRow,
              hover: { backgroundColor: greenRow },
            },
            '& .css.dangerRow': {
              backgroundColor: isDarkMode ? darkDangerBackground : dangerBackground,
              '&:hover': {
                backgroundColor: isDarkMode ? darkDangerBackgroundHover : dangerBackgroundHover,
              },
              '&.Mui-selected': {
                backgroundColor: isDarkMode
                  ? darkDangerBackgroundSelected
                  : dangerBackgroundSelected,
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? darkDangerBackgroundSelectedHover
                    : dangerBackgroundSelectedHover,
                },
              },
            },
          }}
        >
          <DataGridPro
            sortingOrder={DATA_GRID_DEFAULT_SORTING}
            headerFilters={!hideHeaderFilters}
            disableColumnMenu={disableColumnMenu}
            getRowId={getRowId}
            autoHeight={isEmpty(rows)}
            // Commented out 4. 1. 2024 - to all grids look same - stretched vertically to 100% height
            //{...(rows?.length === 0 && autoHeight && { autoHeight: true })} // 30.1.2024 - Make grid autoHeight if there are no rows. To display no records/zadne zaznamy
            sx={{
              ...(onRowClick ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : {}),
              ...(rowHeight
                ? {
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: 0 },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: 0.5 },
                    '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '25.2px' },
                  }
                : {}),
            }}
            getRowClassName={(params) =>
              clsx('css', {
                boldRow: get(params, 'row.state') === NOTIFICATIONS_STATE_NEW,
                greenRow: get(params, 'row.testIsUnfinished', false),
                dangerRow:
                  (get(params, 'row.requestId.status', '') != null &&
                    get(params, 'row.requestId.status', '').toLowerCase() === 'failed') ||
                  (!isObject(get(params, 'row.status', '')) &&
                    get(params, 'row.status', '') !== null &&
                    get(params, 'row.status', '').toLowerCase() === 'nok'),
              })
            }
            //getRowHeight={() => 'auto'}
            rowHeight={DATA_GRID_ROW_HEIGHT}
            density={compactMode ? 'compact' : 'standard'}
            rows={rows}
            columns={useFilterableColumns(gridKey, columns)}
            localeText={gridLocalization}
            disableRowSelectionOnClick={true}
            onRowClick={onRowClick}
            slots={{
              toolbar: toolbar
                ? toolbar
                : disableColumnMenu
                  ? undefined
                  : () =>
                      QuickSearchToolbar({
                        hideGridToolbarColumnsButton: hideGridToolbarColumnsButton || false,
                        hideGridToolbarColumnsFilters: hideGridToolbarColumnsFilters || false,
                      }),
              detailPanelExpandIcon: ChevronRight,
              detailPanelCollapseIcon: ExpandMore,
            }}
            slotProps={{
              columnsPanel: {
                className: 'react-tour-column-menu',
              },
              columnsManagement: {
                getTogglableColumns,
              },
              filterPanel: {
                className: 'react-tour-filter-panel',
              },
              footer: {
                ...(compactMode
                  ? {
                      sx: {
                        minHeight: 30,
                      },
                    }
                  : {}),
              },
              headerFilterCell: {
                InputComponentProps: {
                  size: 'small',
                },
              },
              baseCheckbox: rowHeight
                ? {
                    sx: {
                      p: 0,
                    },
                  }
                : undefined,
            }}
            {...(!rowDetail?.showRowDetail
              ? {}
              : {
                  ...(rowDetail?.DetailPanel && {
                    getDetailPanelContent: rowDetail.DetailPanel,
                  }),
                  ...(rowDetail?.getDetailPanelHeight && {
                    getDetailPanelHeight: rowDetail.getDetailPanelHeight,
                  }),
                  ...(rowDetail?.detailPanelExpandedRowIds && {
                    detailPanelExpandedRowIds: rowDetail.detailPanelExpandedRowIds,
                  }),
                  ...(rowDetail?.handleDetailPanelExpandedRowIdsChange && {
                    onDetailPanelExpandedRowIdsChange:
                      rowDetail.handleDetailPanelExpandedRowIdsChange,
                  }),
                })}
            {...(!rowSelecting?.selecting
              ? {}
              : {
                  checkboxSelection: rowSelecting?.selecting,
                  ...(rowSelecting?.setSelection && {
                    onRowSelectionModelChange: (newSelectionModel: any) =>
                      rowSelecting?.setSelection(newSelectionModel),
                  }),
                  ...(rowSelecting?.selection && {
                    rowSelectionModel: rowSelecting?.selection,
                  }),
                })}
            // used for initial selection of rows) https://github.com/mui/mui-x/issues/5807
            keepNonExistentRowsSelected={rowSelecting?.loadInitialSelection || false}
            columnVisibilityModel={columnVisibilityModelWithDefaultHidden}
            onColumnVisibilityModelChange={(newModel: any) => {
              // store visibility in settings
              setColumnVisibilityModel(newModel);

              // If user hid some columns, remove any active filters for those columns
              try {
                const hiddenCols = compact(
                  keys(pickBy(newModel, (item: Boolean) => item === false)),
                );
                if (
                  hiddenCols.length > 0 &&
                  filterModel &&
                  Array.isArray(filterModel.items) &&
                  filterModel.items.length > 0
                ) {
                  const items = filterModel.items.filter(
                    (filterItem: any) => !hiddenCols.includes(filterItem.field),
                  );
                  if (items.length !== filterModel.items.length) {
                    // update local filter model to remove filters for hidden columns
                    setFilterModel({ items });
                  }
                }
              } catch (error) {
                console.error('Error while removing filters for hidden columns:', error);
              }
            }}
            onColumnWidthChange={(changedColumn: any) => changeWidthColumn(columns, changedColumn)}
            onColumnOrderChange={(params: any) =>
              updateColumnOrder(columns, params, indexShiftForReordering)
            }
            sortModel={
              isArray(gridSortModel) && !isEmpty(gridSortModel)
                ? gridSortModel
                : initialSortMode
                  ? initialSortMode
                  : []
            }
            onSortModelChange={sortByColumn}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            hideFooter={hideFooter}
            onCellEditStop={(params: GridCellEditStopParams, event: MuiEvent) => {
              if (params.reason === GridCellEditStopReasons.enterKeyDown) {
                event.defaultMuiPrevented = true;
              }
            }}
          />
        </Box>
      </Papeer>
      <Box data-pw={`${gridKey}-rows-count`} sx={{ display: 'none' }}>
        {rows.length || 0}
      </Box>
    </Box>
  );
};
